import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { AlertTriangle, CheckCircle2, ExternalLink, Flag, MessageSquare, RefreshCw, Search, Target, TrendingUp, X, XCircle } from "lucide-react";
import { AppShell } from "@/components/AppShell";
import { getTrelloBoard } from "@/lib/trello.functions";
import type { TrelloBoardData } from "@/lib/trello-types";
import { IRL_KEYS, IRL_LABELS, parseTrelloCard, type ParsedReport } from "@/lib/trello-parser";

export const Route = createFileRoute("/trello-insikter")({
  head: () => ({
    meta: [
      { title: "Trello-insikter — Inkubera" },
      { name: "description", content: "Visualiseringar av månadsrapporter och IRL-mognad från Trello." },
    ],
  }),
  loader: () => getTrelloBoard(),
  component: TrelloInsikterPage,
});

function TrelloInsikterPage() {
  const initial = Route.useLoaderData() as { data: TrelloBoardData | null; error: string | null };

  const [data, setData] = useState<TrelloBoardData | null>(initial.data);
  const [error, setError] = useState<string | null>(initial.error);
  const [lastSuccess, setLastSuccess] = useState<string | null>(initial.data?.fetchedAt ?? null);
  const [lastAttempt, setLastAttempt] = useState<string>(new Date().toISOString());
  const [lastStatus, setLastStatus] = useState<"ok" | "failed">(
    initial.data && !initial.error ? "ok" : "failed",
  );
  const [refreshing, setRefreshing] = useState(false);

  async function refresh() {
    setRefreshing(true);
    try {
      const res = await getTrelloBoard();
      setLastAttempt(new Date().toISOString());
      if (res.error || !res.data) {
        setError(res.error ?? "Okänt fel.");
        setLastStatus("failed");
      } else {
        setData(res.data);
        setError(null);
        setLastStatus("ok");
        setLastSuccess(res.data.fetchedAt);
      }
    } catch (e) {
      setLastAttempt(new Date().toISOString());
      setError(e instanceof Error ? e.message : "Nätverksfel");
      setLastStatus("failed");
    } finally {
      setRefreshing(false);
    }
  }

  const allReports = useMemo<ParsedReport[]>(
    () => (data?.cards ?? []).map(parseTrelloCard),
    [data],
  );
  const listName = useMemo(() => {
    const m = new Map<string, string>();
    data?.lists.forEach((l) => m.set(l.id, l.name));
    return m;
  }, [data]);

  // ----- Filter state -----
  const [query, setQuery] = useState("");
  const [activeLists, setActiveLists] = useState<Set<string>>(new Set());

  const toggleList = (id: string) =>
    setActiveLists((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const clearFilters = () => {
    setQuery("");
    setActiveLists(new Set());
  };

  const reports = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allReports.filter((r) => {
      if (activeLists.size > 0 && !activeLists.has(r.listId)) return false;
      if (q && !r.cardName.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [allReports, query, activeLists]);

  const withIRL = reports.filter((r) => r.irlAvg !== undefined);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected =
    withIRL.find((r) => r.cardId === selectedId) ?? withIRL[0];

  // Aggregate radar across (filtered) portfolio
  const portfolioRadar = IRL_KEYS.map((k) => {
    const vals = withIRL.map((r) => r.irl[k]).filter((v): v is number => typeof v === "number");
    const avg = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
    return { dimension: k, label: IRL_LABELS[k], avg: Number(avg.toFixed(2)) };
  });

  const irlBarData = withIRL
    .slice()
    .sort((a, b) => (b.irlAvg ?? 0) - (a.irlAvg ?? 0))
    .map((r) => ({
      name: shortName(r.cardName),
      avg: Number((r.irlAvg ?? 0).toFixed(2)),
      cardId: r.cardId,
    }));

  const exitYes = reports.filter((r) => r.positivExit === true).length;
  const exitNo = reports.filter((r) => r.positivExit === false).length;
  const utmaningar = reports.filter((r) => r.utmaningar);
  const avvikelser = reports.filter((r) => r.avvikelser);

  const filtersActive = query.trim().length > 0 || activeLists.size > 0;

  return (
    <AppShell>
      <section className="px-8 pt-10 pb-6 border-b border-border">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-2">
              Insikter från månadsrapportering
            </div>
            <h1 className="font-display text-4xl">Trello-insikter</h1>
            <p className="text-muted-foreground mt-2 max-w-2xl">
              Tolkar strukturerade rubriker (Milestoneplan, Avvikelser, Utmaningar, Feedback,
              IRL-nivåer) från Trello-korten och visualiserar mognadsgrad, läge och risker i
              portföljen.
            </p>
          </div>
          <SyncStatusPanel
            status={lastStatus}
            lastSuccess={lastSuccess}
            lastAttempt={lastAttempt}
            error={error}
            refreshing={refreshing}
            onRefresh={refresh}
          />
        </div>
        {error && data && (
          <div className="mt-4 p-3 rounded-lg border border-amber-500/40 bg-amber-500/10 text-xs text-amber-700 dark:text-amber-300 flex items-start gap-2">
            <AlertTriangle className="size-3.5 mt-0.5 shrink-0" />
            <span>
              Visar senast kända data från {lastSuccess ? new Date(lastSuccess).toLocaleString("sv-SE") : "—"}. Senaste försök misslyckades: {error}
            </span>
          </div>
        )}
        {error && !data && (
          <div className="mt-4 p-4 rounded-xl border border-destructive/40 bg-destructive/10 text-sm">
            <div className="font-medium text-destructive flex items-center gap-2">
              <AlertTriangle className="size-4" /> Trello-synk misslyckades
            </div>
            <div className="mt-1 text-foreground/80">{error}</div>
          </div>
        )}
      </section>

      <section className="px-8 pt-6 flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-64 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Sök bolagsnamn…"
              className="w-full h-10 pl-9 pr-9 rounded-lg bg-secondary border border-transparent focus:border-ring focus:outline-none text-sm"
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 size-6 rounded-md hover:bg-background/60 flex items-center justify-center"
                aria-label="Rensa sökning"
              >
                <X className="size-3.5 text-muted-foreground" />
              </button>
            )}
          </div>
          {filtersActive && (
            <button
              onClick={clearFilters}
              className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-4"
            >
              Rensa filter ({reports.length}/{allReports.length} bolag)
            </button>
          )}
        </div>

        {data && data.lists.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {data.lists.map((l) => {
              const count = allReports.filter((r) => r.listId === l.id).length;
              const active = activeLists.has(l.id);
              return (
                <button
                  key={l.id}
                  onClick={() => toggleList(l.id)}
                  className={`text-xs px-3 h-8 rounded-full border transition-colors ${
                    active
                      ? "bg-foreground text-background border-foreground"
                      : "bg-card border-border text-muted-foreground hover:text-foreground hover:border-foreground/40"
                  }`}
                >
                  {l.name}
                  <span className={`ml-2 ${active ? "opacity-70" : "opacity-50"}`}>{count}</span>
                </button>
              );
            })}
          </div>
        )}
      </section>

      <section className="p-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi icon={Target} label="Bolag med IRL-rapport" value={`${withIRL.length}/${reports.length}`} />
        <Kpi
          icon={TrendingUp}
          label="Snitt IRL-mognad"
          value={
            withIRL.length
              ? (withIRL.reduce((s, r) => s + (r.irlAvg ?? 0), 0) / withIRL.length).toFixed(1)
              : "—"
          }
        />
        <Kpi icon={Flag} label="Positiv exit klar" value={`${exitYes} ja · ${exitNo} nej`} />
        <Kpi icon={AlertTriangle} label="Lyfter utmaning" value={utmaningar.length.toString()} />
      </section>

      <section className="px-8 pb-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="rounded-2xl border border-border bg-card p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-lg">IRL-snitt per bolag</h2>
            <span className="text-xs text-muted-foreground">Klicka för att se radar</span>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={irlBarData} margin={{ top: 10, right: 10, left: -10, bottom: 50 }}>
                <CartesianGrid stroke="oklch(0.92 0 0)" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" angle={-25} textAnchor="end" height={60} tick={{ fontSize: 11 }} />
                <YAxis domain={[0, 9]} tick={{ fontSize: 11 }} />
                <Tooltip cursor={{ fill: "oklch(0.95 0 0 / 0.5)" }} />
                <Bar dataKey="avg" radius={[6, 6, 0, 0]}>
                  {irlBarData.map((d, i) => (
                    <Cell
                      key={d.cardId}
                      fill={d.cardId === selected?.cardId ? "oklch(0.55 0.2 260)" : "oklch(0.7 0.15 260)"}
                      onClick={() => setSelectedId(d.cardId)}
                      style={{ cursor: "pointer" }}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card p-6">
          <h2 className="font-display text-lg mb-1">
            {selected ? selected.cardName : "Mognadsradar"}
          </h2>
          <p className="text-xs text-muted-foreground mb-3">
            {selected ? "Markerat bolag vs portföljsnitt" : "Portföljsnitt"}
          </p>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart
                data={IRL_KEYS.map((k) => ({
                  dimension: k,
                  bolag: selected?.irl[k] ?? 0,
                  snitt: portfolioRadar.find((p) => p.dimension === k)?.avg ?? 0,
                }))}
              >
                <PolarGrid stroke="oklch(0.9 0 0)" />
                <PolarAngleAxis dataKey="dimension" tick={{ fontSize: 12 }} />
                <PolarRadiusAxis domain={[0, 9]} tick={{ fontSize: 10 }} />
                {selected && (
                  <Radar name="Bolag" dataKey="bolag" stroke="oklch(0.55 0.2 260)" fill="oklch(0.55 0.2 260)" fillOpacity={0.35} />
                )}
                <Radar name="Portföljsnitt" dataKey="snitt" stroke="oklch(0.7 0.15 30)" fill="oklch(0.7 0.15 30)" fillOpacity={0.2} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Tooltip />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      <section className="px-8 pb-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ReportList
          title="Utmaningar att lyfta"
          icon={AlertTriangle}
          items={utmaningar.map((r) => ({
            id: r.cardId,
            url: r.cardUrl,
            name: r.cardName,
            list: listName.get(r.listId) ?? "",
            text: r.utmaningar!,
          }))}
          tone="warning"
        />
        <ReportList
          title="Avvikelser"
          icon={Flag}
          items={avvikelser.map((r) => ({
            id: r.cardId,
            url: r.cardUrl,
            name: r.cardName,
            list: listName.get(r.listId) ?? "",
            text: r.avvikelser!,
          }))}
          tone="info"
        />
      </section>

      <section className="px-8 pb-12">
        <h2 className="font-display text-lg mb-4">Alla bolagsrapporter</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {reports.map((r) => (
            <ReportCard key={r.cardId} report={r} listName={listName.get(r.listId) ?? ""} />
          ))}
          {reports.length === 0 && (
            <div className="text-sm text-muted-foreground">Inga kort hittades.</div>
          )}
        </div>
      </section>
    </AppShell>
  );
}

function shortName(s: string) {
  return s.length > 24 ? s.slice(0, 22) + "…" : s;
}

function Kpi({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider">
        <Icon className="size-3.5" /> {label}
      </div>
      <div className="font-display text-2xl mt-2">{value}</div>
    </div>
  );
}

function ReportList({
  title,
  icon: Icon,
  items,
  tone,
}: {
  title: string;
  icon: React.ElementType;
  items: { id: string; url: string; name: string; list: string; text: string }[];
  tone: "warning" | "info";
}) {
  const toneClass =
    tone === "warning"
      ? "text-amber-700 dark:text-amber-400"
      : "text-sky-700 dark:text-sky-400";
  return (
    <div className="rounded-2xl border border-border bg-card p-6">
      <div className={`flex items-center gap-2 mb-4 ${toneClass}`}>
        <Icon className="size-4" />
        <h2 className="font-display text-lg text-foreground">{title}</h2>
        <span className="text-xs text-muted-foreground ml-auto">{items.length} st</span>
      </div>
      <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
        {items.map((it) => (
          <a
            key={it.id}
            href={it.url}
            target="_blank"
            rel="noreferrer"
            className="block p-3 rounded-xl border border-border hover:border-foreground/40 transition"
          >
            <div className="flex items-center justify-between gap-2">
              <div className="font-medium text-sm">{it.name}</div>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{it.list}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1.5 whitespace-pre-line line-clamp-4">
              {it.text}
            </p>
          </a>
        ))}
        {items.length === 0 && (
          <div className="text-xs text-muted-foreground/60 text-center py-6">Inget att visa</div>
        )}
      </div>
    </div>
  );
}

function ReportCard({ report, listName }: { report: ParsedReport; listName: string }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5 flex flex-col gap-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{listName}</div>
          <div className="font-medium leading-tight mt-1">{report.cardName}</div>
        </div>
        <a href={report.cardUrl} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-foreground">
          <ExternalLink className="size-4" />
        </a>
      </div>

      {Object.keys(report.irl).length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {IRL_KEYS.map((k) =>
            report.irl[k] !== undefined ? (
              <span
                key={k}
                className="text-[11px] px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground"
              >
                {k} {report.irl[k]}
              </span>
            ) : null,
          )}
          {report.irlAvg !== undefined && (
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-foreground text-background ml-auto">
              snitt {report.irlAvg.toFixed(1)}
            </span>
          )}
        </div>
      )}

      {report.milestoneplan && (
        <Section icon={Target} label="Milestoneplan" text={report.milestoneplan} />
      )}
      {report.avvikelser && (
        <Section icon={Flag} label="Avvikelser" text={report.avvikelser} />
      )}
      {report.utmaningar && (
        <Section icon={AlertTriangle} label="Utmaningar" text={report.utmaningar} tone="warning" />
      )}
      {report.feedback && (
        <Section icon={MessageSquare} label="Feedback" text={report.feedback} />
      )}

      {report.positivExit !== null && (
        <div className="text-xs">
          Positiv exit:{" "}
          <span className={report.positivExit ? "text-emerald-600 font-medium" : "text-muted-foreground"}>
            {report.positivExit ? "Ja" : "Nej"}
          </span>
        </div>
      )}
    </div>
  );
}

function Section({
  icon: Icon,
  label,
  text,
  tone,
}: {
  icon: React.ElementType;
  label: string;
  text: string;
  tone?: "warning";
}) {
  return (
    <div>
      <div
        className={`flex items-center gap-1.5 text-[10px] uppercase tracking-wider mb-1 ${
          tone === "warning" ? "text-amber-700 dark:text-amber-400" : "text-muted-foreground"
        }`}
      >
        <Icon className="size-3" /> {label}
      </div>
      <p className="text-xs text-foreground/80 whitespace-pre-line line-clamp-4">{text}</p>
    </div>
  );
}

function relativeTime(iso: string | null): string {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.round(diff / 1000);
  if (s < 60) return `${s} s sedan`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min sedan`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h sedan`;
  return `${Math.round(h / 24)} d sedan`;
}

function SyncStatusPanel({
  status,
  lastSuccess,
  lastAttempt,
  error,
  refreshing,
  onRefresh,
}: {
  status: "ok" | "failed";
  lastSuccess: string | null;
  lastAttempt: string | null;
  error: string | null;
  refreshing: boolean;
  onRefresh: () => void;
}) {
  const ok = status === "ok";
  return (
    <div
      className={`rounded-2xl border p-4 min-w-72 ${
        ok ? "border-emerald-500/30 bg-emerald-500/5" : "border-destructive/40 bg-destructive/5"
      }`}
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          {ok ? (
            <CheckCircle2 className="size-4 text-emerald-600 dark:text-emerald-400" />
          ) : (
            <XCircle className="size-4 text-destructive" />
          )}
          <span className="text-sm font-medium">
            {ok ? "Synk OK" : "Senaste synk misslyckades"}
          </span>
        </div>
        <button
          onClick={onRefresh}
          disabled={refreshing}
          className="h-8 px-2.5 rounded-md bg-foreground text-background text-xs font-medium flex items-center gap-1.5 hover:opacity-90 disabled:opacity-50"
        >
          <RefreshCw className={`size-3.5 ${refreshing ? "animate-spin" : ""}`} />
          Synka nu
        </button>
      </div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
        <dt className="text-muted-foreground">Senast lyckad</dt>
        <dd className="text-right font-medium" title={lastSuccess ?? ""}>
          {lastSuccess ? relativeTime(lastSuccess) : "Aldrig"}
        </dd>
        <dt className="text-muted-foreground">Senaste försök</dt>
        <dd className="text-right font-medium" title={lastAttempt ?? ""}>
          {relativeTime(lastAttempt)}
        </dd>
      </dl>
      {!ok && error && (
        <div className="mt-3 pt-3 border-t border-destructive/20 text-xs text-destructive break-words">
          {error}
        </div>
      )}
    </div>
  );
}
